import { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Play, Calendar, ArrowRight, BarChart3, Shield, Check } from "lucide-react";
import { GlassyImage } from "@/components/GlassyImage";
import { ClientLogos } from "@/components/ClientLogos";
import { TestimonialCards, ScheduleDemo, FAQ } from "@/components/sections";
import { PartnerForChange } from "@/components/sections/PartnerForChange";
import { BenefitsDiscoverCards } from "@/components/sections/BenefitsDiscoverCards";
import { ResultsAccordion } from "@/components/sections/ResultsAccordion";
import { PageContainer } from "@/components/layouts/MainLayout";
import { AnimatedStat } from "@/components/AnimatedStat";
import performanceLoopImg from "@/assets/images/learned-framework-nl.webp";
import { useLocalizedImage, useLocalizedImages } from "@/hooks/use-localized-image";
import { T, THtml } from "@/components/editable/T";
import { EditableImage } from "@/components/editable/EditableImage";
import { getLocalizedRoutePath } from "@/lib/routeUtils";

import heroImage from "@/assets/images/benefits-hr-hero.webp";
import s1Image from "@/assets/illustrations/hr-benefits-solutions.avif";
import s2Image from "@/assets/illustrations/hr-benefits-functieprofiel.avif";
import s2ImageNl from "@/assets/images/benefits-hr-functieprofiel-nl.webp";
import s2ImageEn from "@/assets/images/benefits-hr-functieprofiel-en.webp";
import s2ImageDe from "@/assets/images/benefits-hr-functieprofiel-de.webp";
import s3Image from "@/assets/illustrations/hr-benefits-evaluatie.avif";
import s3ImageNl from "@/assets/images/benefits-hr-evaluatie-nl.webp";
import s3ImageEn from "@/assets/images/benefits-hr-evaluatie-en.webp";
import s3ImageDe from "@/assets/images/benefits-hr-evaluatie-de.webp";
import s4Image from "@/assets/illustrations/hr-benefits-goals.avif";
import s4ImageNl from "@/assets/images/benefits-hr-ontwikkeldoelen-nl.webp";
import s4ImageEn from "@/assets/images/benefits-hr-ontwikkeldoelen-en.webp";
import s4ImageDe from "@/assets/images/benefits-hr-ontwikkeldoelen-de.webp";
import s5Image from "@/assets/illustrations/hr-benefits-9grid.avif";
import s5ImageNl from "@/assets/images/benefits-hr-9grid-nl.webp";
import s5ImageEn from "@/assets/images/benefits-hr-9grid-en.webp";
import s5ImageDe from "@/assets/images/benefits-hr-9grid-de.webp";
import s6Image from "@/assets/illustrations/hr-benefits-customer-success.avif";
import resultsImage from "@/assets/images/benefits-hr-results.webp";




const PAGE = "benefitsHr";

const frameworkStats = [
  { value: 14, suffix: "%", labelKey: "performanceLoop.stat1", labelDefault: "minder verloop" },
  { value: 13, suffix: "%", labelKey: "performanceLoop.stat2", labelDefault: "betere performance" },
  { value: 60, suffixKey: "performanceLoop.stat3suffix", suffixDefault: " uur", labelKey: "performanceLoop.stat3", labelDefault: "besparing voor HR per jaar" },
  { value: 18, suffix: "%", labelKey: "performanceLoop.stat4", labelDefault: "meer tevredenheid" },
];

const featureColors = ["#F3EDFD", "#FDF2ED", "#FFFBEB", "#FAEDFD", "#E8F5E9", "#FFF3E0"];

const BenefitsHr = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'nl';
  const statsRef = useRef<HTMLElement>(null);
  const [statsInView, setStatsInView] = useState(false);

  useEffect(() => {
    const el = statsRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStatsInView(true); observer.disconnect(); } },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const heroImg = useLocalizedImage('benefitsHr', 'hero', 'hero-image', heroImage, { preload: true });
  const featureImages = useLocalizedImages('benefitsHr', 'features',
    ['s1-image', 's2-image', 's3-image', 's4-image', 's5-image', 's6-image'],
    {
      's1-image': s1Image,
      's2-image': lang === 'en' ? s2ImageEn : lang === 'de' ? s2ImageDe : s2ImageNl,
      's3-image': s3Image,
      's4-image': s4Image,
      's5-image': lang === 'nl' ? s5ImageNl : s5Image,
      's6-image': s6Image,
    }
  );

  const features = [
    {
      eyebrowKey: "features.s2.eyebrow",
      eyebrowDefault: "Functiehuis",
      titleKey: "features.s2.title",
      titleDefault: "Maak verwachtingen transparant en geef inzicht in groeipaden",
      bodyKey: "features.s2.body",
      bodyDefault: "Leg de basis voor objectieve evaluaties met moderne functieprofielen op basis van skills en KPI's. Zo heb je ruim 2.000 functieprofielen tot je beschikking. Daarnaast stippel je gemakkelijk een groeipad uit voor alle werknemers.",
      imageKey: 's2-image',
      imgSrc: lang === 'en' ? s2ImageEn : lang === 'de' ? s2ImageDe : s2ImageNl,
      imgAlt: "Functieprofiel aanmaken met AI",
      color: featureColors[1],
      cta: { labelKey: "features.s2.cta", labelDefault: "Ontdek functieprofielen", link: getLocalizedRoutePath("jobProfiles", lang) }
    },
    {
      eyebrowKey: "features.s3.eyebrow",
      eyebrowDefault: "Performance management",
      titleKey: "features.s3.title",
      titleDefault: "Bespaar tijd en voorkom subjectiviteit tijdens evaluaties",
      bodyKey: "features.s3.body",
      bodyDefault: "Maak het voeren van écht goede gesprekken simpeler. Onze AI automatiseert jouw evaluatieproces, zodat je tijd bespaart. Learned verzamelt tussentijdse feedback en vat het samen voor een evaluatie. Zo is er minder sprake van <em>biases</em> en geef je altijd onderbouwde feedback.",
      imageKey: 's3-image',
      imgSrc: lang === 'en' ? s3ImageEn : lang === 'de' ? s3ImageDe : s3ImageNl,
      imgAlt: "Evaluatie invullen met AI",
      color: featureColors[2],
      cta: { labelKey: "features.s3.cta", labelDefault: "Ontdek slimme evaluaties", link: getLocalizedRoutePath("reviews", lang) }
    },
    {
      eyebrowKey: "features.s4.eyebrow",
      eyebrowDefault: "Performance development",
      titleKey: "features.s4.title",
      titleDefault: "Maak het eenvoudig om ontwikkeldoelen op te stellen",
      bodyKey: "features.s4.body",
      bodyDefault: "Leidinggevenden en medewerkers hoeven niet meer te worstelen met het bedenken van ontwikkeldoelen. Laat onze AI-coach ze helpen om SMART-doelen op te stellen. Zo kunnen teamleden zich verder ontwikkelen zonder hulp van HR.",
      imageKey: 's4-image',
      imgSrc: lang === 'en' ? s4ImageEn : lang === 'de' ? s4ImageDe : s4ImageNl,
      imgAlt: "AI-coach helpt bij het opstellen van SMART-doelen",
      color: featureColors[3],
      cta: { labelKey: "features.s4.cta", labelDefault: "Ontdek ontwikkeldoelen", link: getLocalizedRoutePath("developmentGoals", lang) }
    },
    {
      eyebrowKey: "features.s5.eyebrow",
      eyebrowDefault: "Compensation",
      titleKey: "features.s5.title",
      titleDefault: "Krijg de inzichten voor SPP en onderbouwde beslissingen",
      bodyKey: "features.s5.body",
      bodyDefault: "Verzamel alle gegevens die je nodig hebt om de juiste beslissingen te nemen over promoties, uitstroom, beloning en DEI. Ideaal voor strategische personeelsplanning.",
      imageKey: 's5-image',
      imgSrc: lang === 'en' ? s5ImageEn : lang === 'de' ? s5ImageDe : s5ImageNl,
      imgAlt: "9-grid vlootschouw",
      color: featureColors[4],
      cta: { labelKey: "features.s5.cta", labelDefault: "Ontdek HR-rapportage", link: getLocalizedRoutePath("hrReporting", lang) }
    },
  ];


  return (
    <>
      {/* Hero Section */}
      <section className="pt-[140px] pb-6 px-6 relative">
        <PageContainer className="relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-12">
            <div className="animate-fade-in">
              <p className="text-lead text-muted-foreground mb-4">
                <T page={PAGE} k="hero.subtitle" d="Voordelen voor HR" />
              </p>
              <h1 className="mb-6" style={{ color: '#2A2334' }}>
                <T page={PAGE} k="hero.title" d="De inzichten die je nodig hebt voor onderbouwde HR-beslissingen" />
              </h1>
              <p className="text-lead text-muted-foreground mb-8 max-w-xl">
                <THtml page={PAGE} k="hero.body" d="Biedt leidinggevenden een gewogen functiehuis als fundament, verhoog betrokkenheid met heldere carrièrepaden, en neem onderbouwde beslissingen over beloning. Zo bespaar je tijd, en houd je ruimte voor de thema's waar HR het echte verschil maakt." />
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="text-sm px-6 py-5 rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white" onClick={() => document.getElementById('results-accordion')?.scrollIntoView({ behavior: 'smooth' })}>
                    <T page={PAGE} k="hero.tryTrial" d="Ontdek meer" />
                </Button>
                <Button variant="outline" size="lg" className="text-sm px-6 py-5 rounded-full bg-white hover:bg-gray-50" onClick={() => document.getElementById('schedule-demo')?.scrollIntoView({ behavior: 'smooth' })}>
                  <Calendar className="mr-2 h-4 w-4" style={{ color: '#9055EE' }} />
                  <T page={PAGE} k="hero.planDemo" d="Plan een demo" />
                </Button>
              </div>
            </div>
            <div className={`animate-fade-in relative transition-opacity duration-300 ${heroImg.loading ? 'opacity-0' : 'opacity-100'}`} style={{ animationDelay: '200ms' }}>
              <EditableImage page={PAGE} section="hero" imageKey="hero-image" src={heroImg.imageUrl} fallbackSrc={heroImage}>
                <GlassyImage src={heroImg.imageUrl || heroImage} alt={heroImg.altText || "HR benefits"} className="w-full h-[300px] sm:h-[450px] object-cover" imageClassName="object-[72%_28%]" />
              </EditableImage>
              <div className="absolute -right-6 top-4 bg-white rounded-2xl shadow-xl ring-[8px] ring-white/80 p-4 transform rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-3 opacity-0 animate-fade-float-in" style={{ animationDelay: '800ms', animationFillMode: 'forwards' }}>
                <div className="w-10 h-10 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0"><BarChart3 className="w-5 h-5 text-green-600" /></div>
                <div>
                  <T page={PAGE} k="hero.cards.insightLabel" d="Data-gedreven" as="p" className="text-xs text-gray-500 font-medium" />
                  <p className="text-lg font-bold text-green-600">100%</p>
                </div>
              </div>
              <div className="absolute -left-6 bottom-8 bg-white rounded-2xl shadow-xl ring-[8px] ring-white/80 p-4 transform -rotate-2 hover:rotate-0 transition-transform duration-300 hidden lg:flex items-center gap-3 opacity-0 animate-fade-float-in" style={{ animationDelay: '1100ms', animationFillMode: 'forwards' }}>
                <div className="w-10 h-10 rounded-full bg-[#F3EDFD] flex items-center justify-center flex-shrink-0"><Shield className="w-5 h-5 text-[#9055EE]" /></div>
                <div>
                  <T page={PAGE} k="hero.cards.objectiveLabel" d="Objectieve evaluaties" as="p" className="text-xs text-gray-500 font-medium" />
                  <p className="text-lg font-bold" style={{ color: '#9055EE' }}>AI</p>
                </div>
              </div>
            </div>
          </div>
          <ClientLogos />
        </PageContainer>
      </section>

      {/* Quote Section */}
      <section className="py-20 px-6" style={{ background: 'linear-gradient(135deg, #2A2334 0%, #4A3660 50%, #2A2334 100%)' }}>
        <PageContainer>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
            <div>
              <T page={PAGE} k="quote.text" d="Van functiehuis tot beloning, alles op één plek" as="h2" className="text-white font-bold leading-tight" style={{ fontSize: 'clamp(2.4rem, 5vw, 3.5rem)' }} />
            </div>
            <div className="bg-white/10 rounded-2xl p-6 lg:p-8">
              <THtml page={PAGE} k="quote.body" d='<p>Spring je veel bij om leidinggevenden te ondersteunen, van functiehuis tot salarisronde? Met Learned leg je eerst het fundament: een gewogen functiehuis waarmee leidinggevenden zelf de handvatten hebben voor goede evaluaties en gesprekken, zonder dat jij achteraf hoeft te corrigeren. We automatiseren de operationele taken in je evaluatie- en beloningsproces, zodat jij tijd overhoudt voor strategisch werk.</p><p class="mt-4">Evaluaties in Learned zijn gebaseerd op objectieve data, zodat je gefundeerde beslissingen neemt over promoties, ontwikkeling, uitstroom en DEI-beleid. En met Compensation worden ook beloningsbeslissingen eerlijk en uitlegbaar, zodat je nooit meer met een salarisvraag blijft zitten waar je geen antwoord op hebt. Zo groeien niet alleen de prestaties van individuele medewerkers, maar van je hele organisatie.</p>' as="div" className="text-white/80 text-base leading-relaxed" />
            </div>
          </div>
        </PageContainer>
      </section>

      {/* Results Accordion */}
      <div id="results-accordion">
      <ResultsAccordion
        page={PAGE}
        titleKey="results.title"
        titleDefault="De voordelen van één doorlopend, objectief en geautomatiseerd HR-proces"
        items={[
          {
            titleKey: "results.s1.title",
            titleDefault: "Verbeter de prestaties binnen jouw organisatie",
            bodyKey: "results.s1.body",
            bodyDefault: "Stimuleer de ontwikkeling van skills en leg de focus op groei, door objectieve gesprekken, duidelijke opvolging en transparante carrièrepaden. Wanneer verwachtingen helder zijn en evaluaties op data zijn gebaseerd, weten medewerkers precies waar ze aan toe zijn, en krijgen leidinggevenden concrete aanknopingspunten om bij te sturen. Zo verbeteren niet alleen individuele prestaties, maar groeit de prestatie van de hele organisatie mee.",
            ctaKey: "results.s1.cta",
            ctaDefault: "Ontdek onze oplossing",
            link: getLocalizedRoutePath("decisions", lang),
            image: resultsImage,
            imageKey: "results-s1",
          },
          {
            titleKey: "results.s2.title",
            titleDefault: "Verhoog de betrokkenheid en retentie van medewerkers",
            bodyKey: "results.s2.body",
            bodyDefault: "Houd medewerkers uitgedaagd en betrokken met HR-software die ruimte maakt voor duidelijke verwachtingen en hun ontwikkeling. Onduidelijkheid over groeimogelijkheden of beloning is een van de grootste redenen waarom talent vertrekt. Met heldere carrièrepaden en uitlegbare beloningsbeslissingen geef je mensen een reden om te blijven.",
            ctaKey: "results.s2.cta",
            ctaDefault: "Ontdek onze oplossing",
            link: getLocalizedRoutePath("satisfactionEngagement", lang),
            image: resultsImage,
            imageKey: "results-s2",
          },
          {
            titleKey: "results.s3.title",
            titleDefault: "Bespaar je HR-team en leidinggevenden veel tijd",
            bodyKey: "results.s3.body",
            bodyDefault: "Maak gebruik van onze AI-features zoals het automatisch verzamelen van feedback en het voorbereiden van evaluatiegesprekken. Geen losse Excel-sheets meer bij salarisrondes, geen managers die je moet achtervolgen om evaluaties af te ronden, en geen weken uitzoekwerk bij elke salarisvraag. Zo bespaar je niet alleen leidinggevenden veel tijd, tot 32 uur per jaar, maar is ook je HR-team minder tijd kwijt aan operationele taken.",
            ctaKey: "results.s3.cta",
            ctaDefault: "Ontdek onze oplossing",
            link: getLocalizedRoutePath("savingTime", lang),
            image: resultsImage,
            imageKey: "results-s3",
          },
        ]}
      />
      </div>


      {/* Learned Framework + Stats */}
      <section
        ref={statsRef}
        className="py-20 px-6"
        style={{
          background: "linear-gradient(135deg, hsl(270 60% 97%) 0%, hsl(270 80% 94% / 0.5) 50%, white 100%)",
        }}
      >
        <PageContainer>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mb-20">
            <div>
              <p className="text-sm font-semibold uppercase tracking-wide mb-4" style={{ color: "#9055EE" }}>
                <T page={PAGE} k="performanceLoop.eyebrow" d="Learned Framework" />
              </p>
              <h2 className="feature-section-heading mb-6" style={{ color: "#2A2334" }}>
                <T page={PAGE} k="performanceLoop.title" d="De methode om objectief HR-beleid organisatiebreed te borgen" />
              </h2>
              <THtml
                page={PAGE}
                k="performanceLoop.body"
                d="Het Learned Framework verbindt functiehuis, Performance Management en beloning tot één doorlopend proces. Zo bouw je een gewogen fundament, evalueer en ontwikkel je objectief, en beloon je eerlijk, altijd op basis van dezelfde, vastgelegde verwachtingen."
                as="div"
                className="text-lead text-muted-foreground"
              />
              <ul className="mt-8 flex flex-col gap-3 text-left">
                {[
                  { k: "performanceLoop.check1", d: "Eén methodiek, consistent toegepast in de hele organisatie" },
                  { k: "performanceLoop.check2", d: "Geen tijdrovende Excel-sheets meer bij salarisrondes" },
                  { k: "performanceLoop.check3", d: "Doorlopend inzicht in wie waar staat, zonder losse rapportages" },
                  { k: "performanceLoop.check4", d: "Compliant met de wet Loontransparantie" },
                ].map((item) => (
                  <li key={item.k} className="flex items-start gap-3">
                    <Check className="h-5 w-5 shrink-0 mt-0.5" style={{ color: "#9055EE" }} />
                    <span className="text-muted-foreground">
                      <T page={PAGE} k={item.k} d={item.d} />
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center justify-center">
              <img
                src={performanceLoopImg}
                alt="Learned Framework: Work design, Performance, Development en Compensation"
                className="w-full h-auto lg:scale-110 lg:origin-left"
                loading="lazy"
              />
            </div>
          </div>

          <div className="border-t border-border/40 pt-16 text-center">
            <p className="text-muted-foreground text-sm mb-10">
              <T page={PAGE} k="performanceLoop.statsTitle" d="De resultaten van onze 350+ klanten" />
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {frameworkStats.map((stat, i) => {
                const suffix = stat.suffixKey ? (t(`${PAGE}.${stat.suffixKey}`, { defaultValue: stat.suffixDefault }) as string) : stat.suffix;
                return (
                  <div key={i} className="flex flex-col items-center gap-2">
                    <span className="block text-4xl md:text-5xl lg:text-6xl font-bold" style={{ color: '#2A2334' }}>
                      {statsInView ? (
                        <AnimatedStat value={stat.value} suffix={suffix} delay={300 + i * 250} duration={3500} />
                      ) : (
                        <span>0{suffix}</span>
                      )}
                    </span>
                    <p className="text-sm text-muted-foreground">
                      <T page={PAGE} k={stat.labelKey} d={stat.labelDefault} />
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </PageContainer>
      </section>


      {/* Features Section */}
      <section className="pt-[60px] pb-20 px-6">
        <PageContainer>
          <div className="space-y-16">
            {features.map((feature, index) => {
              const isReversed = index % 2 === 1;
              return (
                <div
                  key={index}
                  className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center animate-fade-in"
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  <div className={`${isReversed ? 'lg:order-2' : 'lg:order-1'}`}>
                    {feature.eyebrowKey && (
                      <p className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: "#9055EE" }}>
                        <T page={PAGE} k={feature.eyebrowKey} d={feature.eyebrowDefault} />
                      </p>
                    )}
                    <h2 className="feature-section-heading text-foreground">
                      <T page={PAGE} k={feature.titleKey} d={feature.titleDefault} />
                    </h2>
                    <THtml page={PAGE} k={feature.bodyKey} d={feature.bodyDefault} as="p" className="text-lead text-muted-foreground" />

                    {feature.cta && (
                      <Button
                        className="mt-6 rounded-full bg-[#9055EE] hover:bg-[#753DCE] text-white"
                        onClick={() => navigate(feature.cta!.link)}
                      >
                        <T page={PAGE} k={feature.cta.labelKey} d={feature.cta.labelDefault} />
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className={`${isReversed ? 'lg:order-1' : 'lg:order-2'} relative`}>
                    {('imgSrc' in feature ? (feature as { imgSrc?: string }).imgSrc : undefined) ? (
                      <EditableImage
                        page={PAGE}
                        section="features"
                        imageKey={feature.imageKey}
                        src={(feature as { imgSrc?: string }).imgSrc || ''}
                        alt={('imgAlt' in feature ? (feature as { imgAlt?: string }).imgAlt : undefined) || ''}
                        loading="lazy"
                        className="w-full h-auto rounded-2xl"
                      />
                    ) : (
                    <div
                      className="rounded-2xl p-8 flex items-center justify-center relative overflow-hidden max-h-[450px]"
                      style={{
                        background: `radial-gradient(circle at center, white 0%, ${feature.color} 70%)`,
                        boxShadow: 'inset 0 0 0 10px rgba(255, 255, 255, 0.7)'
                      }}
                    >
                      <EditableImage
                        page={PAGE}
                        section="features"
                        imageKey={feature.imageKey}
                        src={featureImages[feature.imageKey]?.imageUrl || ''}
                        alt={featureImages[feature.imageKey]?.altText || ''}
                        className="w-full max-w-md max-h-[400px] object-contain relative z-10"
                      />
                    </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </PageContainer>
      </section>

      <PartnerForChange 
        page="benefitsHr"
        titleKey="partner.title"
        titleDefault="Begeleiding bij je implementatie en betere HR-beslissingen"
        bodyKey="partner.body"
        bodyDefault="Rol Learned uit met een implementatieplan op maat, zodat je direct een sterke basis legt voor objectieve evaluaties en betere HR-beslissingen. We helpen je om processen slim in te richten, managers mee te nemen en meer uit je data te halen. Ook daarna ondersteunen we je met een vaste <em>Performance Specialist</em> en trainingen voor HR en managers."
      />


      <ScheduleDemo />

      <FAQ
        className="mx-0 mb-[-96px] pb-[192px] rounded-none"
        style={{ background: 'linear-gradient(135deg, #2A2334 0%, #4A3660 50%, #2A2334 100%)' }}
        dark
        page={PAGE}
        titleKey="benefitsHr.faq.title"
        titleDefault="Veelgestelde vragen"
        subtitleKey="benefitsHr.faq.subtitle"
        subtitleDefault="Heb je nog vragen? Hier vind je antwoorden op de meestgestelde vragen over HR-voordelen."
        items={[
          {
            questionKey: "benefitsHr.faq.q1.question",
            questionDefault: "Hoe helpt Learned HR-teams efficiënter werken?",
            answerKey: "benefitsHr.faq.q1.answer",
            answerDefault: "Learned automatiseert operationele HR-taken zoals het verzamelen van feedback, het voorbereiden van evaluatiegesprekken en het bijhouden van ontwikkeldoelen. Zo houdt je HR-team meer tijd over voor strategisch werk."
          },
          {
            questionKey: "benefitsHr.faq.q2.question",
            questionDefault: "Welke inzichten krijg ik voor personeelsbeslissingen?",
            answerKey: "benefitsHr.faq.q2.answer",
            answerDefault: "Met de HR-rapportages en 9-grid analyse krijg je inzicht in prestaties, potentieel, skills gaps en betrokkenheid. Ideaal voor beslissingen over promoties, beloning, uitstroom en DEI."
          },
          {
            questionKey: "benefitsHr.faq.q3.question",
            questionDefault: "Hoe objectief zijn de evaluaties in Learned?",
            answerKey: "benefitsHr.faq.q3.answer",
            answerDefault: "Evaluaties zijn gebaseerd op competenties, skills en KPI's uit het functiehuis. Onze AI verzamelt tussentijdse feedback en vat deze samen, waardoor biases worden verminderd en gesprekken altijd onderbouwd zijn."
          },
          {
            questionKey: "benefitsHr.faq.q4.question",
            questionDefault: "Krijg ik ondersteuning bij de implementatie?",
            answerKey: "benefitsHr.faq.q4.answer",
            answerDefault: "Ja, je krijgt een implementatieplan op maat en een vaste Performance Specialist. Daarnaast bieden we HR- en managerstrainingen aan voor blijvende impact."
          },
          {
            questionKey: "benefitsHr.faq.q5.question",
            questionDefault: "Kan ik Learned koppelen aan ons HRIS?",
            answerKey: "benefitsHr.faq.q5.answer",
            answerDefault: "Ja, Learned integreert naadloos met populaire HR-core en HRIS-systemen. Zo synchroniseer je medewerkergegevens automatisch en voorkom je dubbel werk."
          }
        ]}
      />
    </>
  );
};

export default BenefitsHr;
